'use client';

import type { CreditRangeEnum } from '@cc-shopping/contracts/credit-cards-page/roundup/utils';
import { usePageAction } from '@cc-shopping/platform-analytics/hooks/usePageAction';
import type { MobileFilterCloseReason } from 'src/features/roundup-filters/types';
import { useShallow } from 'zustand/shallow';
import { useOffersFilterStore } from '../../Context';
import { type FilterOffersByType, FilterType, type SortOffersByType } from '../../enums';
import type { OffersFilterStoreState } from '../../types';
import { getLabelForFilterItem } from '../../utils';

export function useFiltersAnalytics() {
  const {
    preComputedFilterOfferIds: { issuersIdNameMap },
  } = useOffersFilterStore(
    useShallow((state: OffersFilterStoreState) => ({
      preComputedFilterOfferIds: state.preComputedFilterOfferIds,
    })),
  );
  const firePageAction = usePageAction({
    actionSection: 'roundup_filters',
  });

  const fireCardFeatureUpdateEvents = (value: FilterOffersByType) => {
    const label = getLabelForFilterItem({
      type: FilterType.CARD_FEATURE,
      item: value,
      issuersIdNameMap,
    });
    firePageAction({
      action_name: 'card_feature_filter',
      action_input: label,
      action_type: 'select',
    });
  };

  const fireSortByUpdateEvents = (value: SortOffersByType) => {
    const label = getLabelForFilterItem({
      type: FilterType.SORT_BY,
      item: value,
      issuersIdNameMap,
    });
    firePageAction({
      action_name: 'sorting_filter',
      action_input: label,
      action_type: 'select',
    });
  };

  const fireIssuerUpdateEvents = ({
    newSelectedValues,
    prevSelectedValues,
  }: {
    newSelectedValues: string[];
    prevSelectedValues: string[];
  }) => {
    const addedValue = newSelectedValues.find(value => !prevSelectedValues.includes(value));
    const removedValue = prevSelectedValues.find(
      prevValue => !newSelectedValues.includes(prevValue),
    );
    if (addedValue) {
      const label = getLabelForFilterItem({
        type: FilterType.ISSUER,
        item: addedValue,
        issuersIdNameMap,
      });
      firePageAction({
        action_name: 'issuers_filter',
        action_input: label,
        action_type: 'select',
      });
    } else if (removedValue) {
      const label = getLabelForFilterItem({
        type: FilterType.ISSUER,
        item: removedValue,
        issuersIdNameMap,
      });
      firePageAction({
        action_name: 'issuers_filter',
        action_input: label,
        action_type: 'deselect',
      });
    }
  };

  const fireCreditRangeUpdateEvents = ({
    newSelectedValues,
    prevSelectedValues,
  }: {
    newSelectedValues: CreditRangeEnum[];
    prevSelectedValues: CreditRangeEnum[];
  }) => {
    const addedValue = newSelectedValues.find(selected => !prevSelectedValues.includes(selected));
    const removedValue = prevSelectedValues.find(
      prevSelected => !newSelectedValues.includes(prevSelected),
    );
    if (addedValue) {
      const label = getLabelForFilterItem({
        type: FilterType.CREDIT_RANGE,
        item: addedValue,
        issuersIdNameMap,
      });
      firePageAction({
        action_name: 'credit_range_filter',
        action_input: label,
        action_type: 'select',
      });
    } else if (removedValue) {
      const label = getLabelForFilterItem({
        type: FilterType.CREDIT_RANGE,
        item: removedValue,
        issuersIdNameMap,
      });
      firePageAction({
        action_name: 'credit_range_filter',
        action_input: label,
        action_type: 'deselect',
      });
    }
  };

  const firePillsRemovalEvents = (label: string) => {
    firePageAction({
      action_name: 'clear_filter',
      action_input: label,
      action_type: 'select',
    });
  };

  const fireClearAllEvents = () => {
    firePageAction({
      action_name: 'clear_filter',
      action_input: 'Clear all',
      action_type: 'select',
    });
  };

  const fireApplyFilterEvents = ({ reason }: { reason: MobileFilterCloseReason }) => {
    let actionName;
    switch (reason) {
      case 'apply':
        actionName = 'apply_cta';
        break;
      case 'close':
        actionName = 'close_x';
        break;
      case 'esc_key':
        actionName = 'esc_key';
        break;
    }
    firePageAction({
      action_name: actionName,
      action_input: null,
      action_type: 'select',
    });
  };

  return {
    fireCardFeatureUpdateEvents,
    fireCreditRangeUpdateEvents,
    fireIssuerUpdateEvents,
    fireSortByUpdateEvents,
    firePillsRemovalEvents,
    fireClearAllEvents,
    fireApplyFilterEvents,
  };
}
